<template>
  <DsPageContent>
    <template v-if="showForm">
      <DsHeroWithForm
        v-if="safeCampaign.showFormInHeader"
        :theme="safeCampaign.heroTheme"
        :title="title"
        :eyebrow="safeCampaign.headerEyebrow"
        :links="headerLinks"
        with-title-size-large
        :navigation-data="backLink"
        y-align="stretch"
      >
        <template #description>
          <RichText
            v-if="safeCampaign.headerSubtitle"
            :data="safeCampaign.headerSubtitle"
          />
        </template>
        <template #form>
          <div
            class="form-wrapper ds-padding-24 ds-md-padding-48"
            :class="
              !safeCampaign.heroTheme || safeCampaign.heroTheme === 'grey'
                ? 'ds-background-color-white'
                : 'ds-background-color-grey-100'
            "
          >
            <ContactForm
              :form-id="safeCampaign.formId"
              :custom-form-labels="campaignFormLabels"
              :form-success-redirect-path="safeCampaign?.formSuccessRedirectPage?.url"
              @success="emit('form-success')"
            />
          </div>
        </template>
      </DsHeroWithForm>
      <HeroWithContainerMedia
        v-else-if="!safeCampaign.hasAnimatedHero"
        :title="title"
        :theme="safeCampaign.heroTheme"
        :eyebrow="safeCampaign.headerEyebrow"
        :links="headerLinks"
        :image="campaign.headerImage || headerImage"
        :orientation="headerMediaOrientation"
        :link-back="backLink"
        :screen="screen"
        with-title-size-large
        y-align="stretch"
        aspect-ratio="3/4"
      >
        <template #description>
          <RichText
            v-if="safeCampaign.headerSubtitle"
            :data="safeCampaign.headerSubtitle"
          />
        </template>
      </HeroWithContainerMedia>
      <AnimatedHero
        v-else-if="safeCampaign.hasAnimatedHero"
        :title="title"
        :eyebrow="safeCampaign.headerEyebrow"
        :image="safeCampaign.headerImage || (!safeCampaign.headerVideo ? headerImage : null)"
        :desktop-image="
          safeCampaign.desktopHeaderImage ||
          safeCampaign.headerImage ||
          (!safeCampaign.desktopHeaderVideo && !safeCampaign.headerVideo ? headerImage : null)
        "
        :video="safeCampaign.headerVideo"
        :desktop-video="safeCampaign.desktopHeaderVideo"
        :links="headerLinks"
        :screen="screen"
        :theme="safeCampaign.heroTheme"
      >
        <template #description>
          <RichText
            v-if="safeCampaign.headerSubtitle"
            :data="safeCampaign.headerSubtitle"
            class="ds-text-medium"
          />
        </template>
      </AnimatedHero>

      <CampaignsSection
        v-if="sections.length > 0"
        :sections="sections"
      />

      <DsSection
        v-if="!safeCampaign.showFormInHeader"
        id="campaign-form"
        class="ds-padding-top-48 ds-md-padding-y-72"
        :with-space="false"
        :theme="safeCampaign.heroTheme === 'grey' ? 'light' : 'grey'"
      >
        <CampaignsFormSection
          :title="safeCampaign.formTitle"
          :description="safeCampaign.formDescription"
        >
          <template #form>
            <div
              class="form-wrapper ds-padding-24 ds-md-padding-48"
              :class="safeCampaign.heroTheme === 'grey' ? 'ds-background-color-grey-100' : 'ds-background-color-white'"
            >
              <ContactForm
                :form-id="safeCampaign.formId"
                :custom-form-labels="safeCampaign"
                :form-success-redirect-path="safeCampaign?.formSuccessRedirectPage?.url"
                @success="emit('form-success')"
              />
            </div>
          </template>
        </CampaignsFormSection>
      </DsSection>

      <KnowledgeHubRelatedContent
        :related-content="relatedContent"
        :see-all-label="campaignFormLabels.seeAllKnowledgeHubArticles"
        :see-all-link="parentPage"
      />
    </template>

    <template v-else>
      <DsHero
        v-if="thankYouContent && thankYouContent.title"
        theme="grey"
        :title="thankYouContent.title"
        :text="thankYouContent.description"
        :links="thankYouDownloadLink"
      />
      <CampaignsSection
        v-if="thankYouSections.length > 0"
        :sections="thankYouSections"
      />

      <KnowledgeHubRelatedContent
        v-if="thankYouRelatedContent"
        :related-content="thankYouRelatedContent"
        :see-all-label="campaignFormLabels.seeAllKnowledgeHubArticles"
        :see-all-link="parentPage"
      />
    </template>
  </DsPageContent>
</template>

<script setup>
import { DsPageContent, DsHeroWithForm, DsSection, DsHero } from 'awds'
import HeroWithContainerMedia from '../../../core/components/HeroWithContainerMedia.vue'
import AnimatedHero from '../../../core/components/animated-hero/AnimatedHero.vue'

const { screen } = useScreen()
const { labels } = useKnowledgeHubGlobalData()

const emit = defineEmits(['form-success'])

const props = defineProps({
  title: { type: String, default: '' },
  showForm: { type: Boolean, default: true },
  campaign: { type: Object, default: null },
  parentPage: { type: [Object, String], default: null },
  headerMediaOrientation: { type: String, default: 'right' },
  headerImage: { type: Object, default: null }
})

const safeCampaign = computed(() => props.campaign || {})

const campaignFormLabels = computed(() => {
  return {
    ...labels.value,
    formSubmitLabelContact: safeCampaign.value?.formSubmitLabel || labels.value.formSubmitLabelDownload,
    downloadTitle: labels.value.gatedContentDownloadButton,
    backLinkTitle: labels.value.backLink
  }
})

const ANCHOR_FRAGMENT_URL = '#campaign-form'
const headerLinks = computed(() => {
  if (
    safeCampaign.value &&
    safeCampaign.value.formId &&
    !safeCampaign.value.showFormInHeader &&
    safeCampaign.value.formAnchorLabel
  ) {
    const ANCHOR_LINK = {
      url: ANCHOR_FRAGMENT_URL,
      title: safeCampaign.value.formAnchorLabel,
      openInNewWindow: false,
      nuxt: false
    }
    return [ANCHOR_LINK, safeCampaign.value.headerLink]
  } else {
    return [safeCampaign.value?.headerLink, safeCampaign.value?.secondaryHeaderLink].filter(Boolean)
  }
})

const backLink = computed(() => {
  if (!safeCampaign.value || safeCampaign.value.visibleInKnowledgeHub) {
    return { text: campaignFormLabels.value.backLinkTitle, url: props.parentPage }
  } else {
    return null
  }
})

const sections = computed(() => {
  return safeCampaign.value?.contentBlocks || []
})

const thankYouContent = computed(() => {
  return safeCampaign.value?.thankYouContent
})

const thankYouSections = computed(() => {
  return thankYouContent.value?.contentBlocks || []
})

const thankYouDownloadLink = computed(() => {
  if (thankYouContent.value?.downloadMedia) {
    return [
      {
        ...thankYouContent.value?.downloadMedia?.link,
        type: 'button',
        iconRight: { category: 'arrows', name: 'download' },
        title: campaignFormLabels.value.downloadTitle
      }
    ]
  }
  return []
})

const relatedContent = computed(() => {
  return safeCampaign.value?.relatedContent
})

const thankYouRelatedContent = computed(() => {
  return thankYouContent?.value.relatedContent
})

useAnchorFragmentSmoothScroll(ANCHOR_FRAGMENT_URL)
</script>
